import {
  Alert,
  LinearProgress,
  Menu,
  MenuItem,
  type MenuProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { matchSorter } from 'match-sorter';
import React from 'react';
import { P, match } from 'ts-pattern';
import { type APIResponse, aiChatsQueryOptions } from '#/api';
import { TextSearchFilter } from '#/components/FormFields';

export interface ChatHistoryMenuProps
  extends Omit<MenuProps, 'onSelect' | 'onClose'> {
  /**
   * The ID of the current chat.
   */
  currentChatId?: string;
  /**
   * Callback function to handle the selection of a chat.
   * @param chat - The chat object that was selected.
   */
  onSelect: (chat: Chat) => void;
  /**
   * Callback function to handle the closing of the menu.
   */
  onClose: () => void;
}

function ChatHistoryMenu({
  currentChatId,
  onSelect,
  onClose,
  open,
  className,
  ...props
}: ChatHistoryMenuProps) {
  const [search, setSearch] = React.useState('');
  const chatsQuery = useQuery({
    ...aiChatsQueryOptions(),
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchInterval: 1000 * 60 * 5,
  });

  const filteredChats = React.useMemo(() => {
    if (search) {
      return matchSorter(chatsQuery.data ?? [], search, {
        keys: ['title'],
      });
    }
    return chatsQuery.data ?? [];
  }, [chatsQuery.data, search]);

  const handleClose = React.useCallback(() => {
    setSearch('');
    onClose();
  }, [onClose]);

  const handleSelect = React.useCallback(
    (chat: Chat) => {
      onSelect(chat);
      handleClose();
    },
    [onSelect, handleClose]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only refetch on open change
  React.useEffect(() => {
    if (open && chatsQuery.status === 'success') {
      chatsQuery.refetch();
    }
  }, [open, chatsQuery.status]);

  return (
    <Menu {...props} open={open} onClose={handleClose}>
      <div className='px-2 mb-2 pb-2 border-b border-divider'>
        <TextSearchFilter
          className='mb-0'
          value={search}
          onChange={setSearch}
          size='small'
        />
      </div>
      <div className='max-h-[400px] overflow-y-auto'>
        {match({ ...chatsQuery, data: filteredChats })
          .with(
            { status: 'success', data: P.when((chats) => chats.length > 0) },
            ({ data: chats }) =>
              chats.map((chat) => (
                <MenuItem
                  className='max-w-[300px]'
                  key={chat.id}
                  onClick={() => handleSelect(chat)}
                  dense
                >
                  <Tooltip title={chat.title} placement='left' arrow>
                    <Typography variant='body2' className='text-sm' noWrap>
                      {chat.title}
                      <span className='ml-1 text-xs text-secondary-contrast/50'>
                        {DateTime.fromISO(chat.createdAt).toRelative({
                          style: 'narrow',
                        })}
                      </span>
                    </Typography>
                  </Tooltip>
                  <div className='flex-1 min-w-4' />
                  {currentChatId === chat.chatId && (
                    <Typography
                      variant='body2'
                      className='text-xs text-secondary-contrast/50'
                    >
                      current chat
                    </Typography>
                  )}
                </MenuItem>
              ))
          )
          .with(
            { status: 'success', data: P.when((chats) => chats.length === 0) },
            () => (
              <div className='p-2'>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  className='text-center'
                >
                  No past chats
                </Typography>
              </div>
            )
          )
          .with({ status: 'pending' }, () => (
            <React.Fragment>
              <div className='p-2'>
                <Typography variant='body2' color='text.secondary'>
                  Loading chat history...
                </Typography>
              </div>
              <LinearProgress className='absolute bottom-0 left-0 right-0' />
            </React.Fragment>
          ))
          .otherwise(() => (
            <div className='p-2'>
              <Typography variant='body2' color='text.secondary'>
                <Alert severity='error'>Error loading chat history</Alert>
              </Typography>
            </div>
          ))}
      </div>
    </Menu>
  );
}

export default ChatHistoryMenu;

/*
|------------------
| Internals
|------------------
*/

type Chat = APIResponse<'GET /_prv/ai/chats'>[number];
